import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Building2,
  Check,
  MapPin,
  Plug,
  SlidersHorizontal,
  Store,
} from "lucide-react";
import { toast } from "sonner";

import ModalBase from "components/ModalBase";
import { InputSelect } from "components/CustomInputs/InputSelect";
import LANGUAGES_LIST from "constants/LANGUAGES_LIST";
import { paths } from "app/paths";
import { verConexaoNuvemshop, verConexaoShopify } from "services/api";
import {
  atualizarDestinoFixo,
  atualizarRemetenteFixo,
  atualizarEnderecoEmpresa,
  getClienteAtual,
  DestinoFixo,
  EnderecoEmpresa,
} from "services/cliente";

type Props = {
  show: boolean;
  title: string;
  setShowNumber: (value: React.SetStateAction<number>) => void;
  logo?: React.ReactNode;
};

type Secao = "geral" | "integracoes" | "empresa" | "remetente" | "destinatario";

const DESTINO_VAZIO: DestinoFixo = {
  destinoFixoNome: "",
  destinoFixoPais: "",
  destinoFixoEstado: "",
  destinoFixoCidade: "",
  destinoFixoRua: "",
  destinoFixoCEP: "",
  destinoFixoTelefone: "",
};

const EMPRESA_VAZIA: EnderecoEmpresa = {
  enderecoPais: "",
  enderecoCEP: "",
  enderecoRua: "",
  enderecoNumero: "",
  enderecoComplemento: "",
  enderecoCidade: "",
  enderecoEstado: "",
  telefoneCelular: "",
};

const ModalSettings = ({ show, title, logo, setShowNumber }: Props) => {
  const { t, i18n } = useTranslation("dashboard");
  const navigate = useNavigate();

  const [secao, setSecao] = useState<Secao>("geral");
  const [idioma, setIdioma] = useState(() => i18n.language);

  const [conta, setConta] = useState<{ nome: string; email: string } | null>(null);

  // `salvo` é a cópia do que está no servidor. O que está em tela compara com
  // ela para saber se há alteração pendente e para o "Descartar" ter para
  // onde voltar.
  const [empresa, setEmpresa] = useState<EnderecoEmpresa>(EMPRESA_VAZIA);
  const [empresaSalva, setEmpresaSalva] = useState<EnderecoEmpresa>(EMPRESA_VAZIA);
  const [destino, setDestino] = useState<DestinoFixo>(DESTINO_VAZIO);
  const [destinoSalvo, setDestinoSalvo] = useState<DestinoFixo>(DESTINO_VAZIO);

  const [remetenteFixoAtivo, setRemetenteFixoAtivo] = useState(true);
  const [salvando, setSalvando] = useState(false);

  const [shopify, setShopify] = useState<{ conectada: boolean; loja: string | null } | null>(null);
  const [nuvemshop, setNuvemshop] = useState<{ conectada: boolean } | null>(null);

  useEffect(() => {
    if (!show) return;

    getClienteAtual().then((res) => {
      setConta({ nome: res.razaoSocial, email: res.emailPrincipal });
      setRemetenteFixoAtivo(res.remetenteFixoAtivo);

      const dadosEmpresa: EnderecoEmpresa = {
        enderecoPais: res.enderecoPais || "",
        enderecoCEP: res.enderecoCEP || "",
        enderecoRua: res.enderecoRua || "",
        enderecoNumero: res.enderecoNumero || "",
        enderecoComplemento: res.enderecoComplemento || "",
        enderecoCidade: res.enderecoCidade || "",
        enderecoEstado: res.enderecoEstado || "",
        telefoneCelular: res.telefoneCelular || "",
      };
      setEmpresa(dadosEmpresa);
      setEmpresaSalva(dadosEmpresa);

      const dadosDestino: DestinoFixo = {
        destinoFixoNome: res.destinoFixoNome || "",
        destinoFixoPais: res.destinoFixoPais || "",
        destinoFixoEstado: res.destinoFixoEstado || "",
        destinoFixoCidade: res.destinoFixoCidade || "",
        destinoFixoRua: res.destinoFixoRua || "",
        destinoFixoCEP: res.destinoFixoCEP || "",
        destinoFixoTelefone: res.destinoFixoTelefone || "",
      };
      setDestino(dadosDestino);
      setDestinoSalvo(dadosDestino);
    });

    // O /shopify/conexao não devolve `connected` — a linha está comentada no
    // backend. Quem diz se está conectada é a presença do shopDomain.
    verConexaoShopify()
      .then(({ data }) => {
        const loja = (data as { loja?: { shopDomain?: string } })?.loja?.shopDomain ?? null;
        setShopify({ conectada: Boolean(loja), loja });
      })
      .catch(() => setShopify({ conectada: false, loja: null }));

    verConexaoNuvemshop()
      .then(({ data }) => setNuvemshop({ conectada: Boolean(data?.connected) }))
      .catch(() => setNuvemshop({ conectada: false }));
  }, [show]);

  const empresaMudou = useMemo(
    () => JSON.stringify(empresa) !== JSON.stringify(empresaSalva),
    [empresa, empresaSalva],
  );
  const destinoMudou = useMemo(
    () => JSON.stringify(destino) !== JSON.stringify(destinoSalvo),
    [destino, destinoSalvo],
  );
  const temAlteracao = empresaMudou || destinoMudou;

  const descartar = () => {
    setEmpresa(empresaSalva);
    setDestino(destinoSalvo);
  };

  /**
   * Um botão para o modal inteiro, como no desenho. Salva só o que mudou —
   * antes cada bloco tinha o próprio botão e a pessoa precisava descobrir
   * qual apertar.
   */
  const salvar = async () => {
    setSalvando(true);
    try {
      if (empresaMudou) {
        await atualizarEnderecoEmpresa(empresa);
        setEmpresaSalva(empresa);
      }
      if (destinoMudou) {
        await atualizarDestinoFixo(destino);
        setDestinoSalvo(destino);
      }
      toast.success("Configurações salvas");
    } catch (err: any) {
      toast.error(
        err?.response?.data?.erro || err?.message || "Falha ao salvar as configurações.",
      );
    } finally {
      setSalvando(false);
    }
  };

  // Salva na hora, sem passar pelo rodapé: é um interruptor, não um
  // formulário. Otimista, desfaz se a API recusar.
  const alternarRemetente = async (ativo: boolean) => {
    setRemetenteFixoAtivo(ativo);
    try {
      await atualizarRemetenteFixo(ativo);
    } catch (err: any) {
      setRemetenteFixoAtivo(!ativo);
      toast.error(err?.response?.data?.erro || "Falha ao salvar a preferência.");
    }
  };

  const trocarIdioma = (valor: string) => {
    if (!valor || valor === i18n.language) return;
    i18n.changeLanguage(valor);
    setIdioma(valor);
  };

  const integracoesConectadas =
    (shopify?.conectada ? 1 : 0) + (nuvemshop?.conectada ? 1 : 0);
  const destinoLigado = Boolean(destinoSalvo.destinoFixoNome.trim());

  const MENU: { chave: Secao; label: string; apoio: string; selo?: ReactNodeSelo }[] = [
    { chave: "geral", label: "Geral", apoio: "Idioma da plataforma" },
    {
      chave: "integracoes",
      label: "Integrações",
      apoio: "Lojas conectadas",
      selo: integracoesConectadas
        ? { texto: String(integracoesConectadas), tom: "verde" }
        : undefined,
    },
    { chave: "empresa", label: "Empresa", apoio: "Endereço e telefone" },
    { chave: "remetente", label: "Remetente", apoio: "Quem envia" },
    {
      chave: "destinatario",
      label: "Destinatário fixo",
      apoio: "Pré-preenchimento",
      selo: destinoLigado ? undefined : { texto: "Desligado", tom: "cinza" },
    },
  ];

  const cabecalho: Record<Secao, { titulo: string; apoio: string }> = {
    geral: { titulo: "Geral", apoio: "Preferências da plataforma." },
    integracoes: {
      titulo: "Integrações",
      apoio: "Conecte sua loja para os pedidos chegarem prontos para cotar.",
    },
    empresa: {
      titulo: "Endereço e telefone da empresa",
      apoio: "Usados como remetente nas cotações e como endereço de coleta.",
    },
    remetente: { titulo: "Remetente", apoio: "De onde saem os seus envios." },
    destinatario: {
      titulo: "Destinatário fixo",
      apoio: "Pré-preenche o destinatário ao criar uma cotação.",
    },
  };

  return (
    <ModalBase
      show={show}
      onClose={() => setShowNumber(-1)}
      titulo={title}
      icone={logo ?? <SlidersHorizontal />}
      largura="1000px"
      corpoSemPadding
      rodape={
        <div className="flex items-center justify-between gap-4 border-t border-slate-100 bg-white px-6 py-3.5">
          <p className="flex items-center gap-2 text-xs">
            <span
              className={`h-1.5 w-1.5 rounded-full ${
                temAlteracao ? "bg-amber-500" : "bg-emerald-500"
              }`}
            />
            <span className={temAlteracao ? "text-amber-700" : "text-slate-500"}>
              {temAlteracao ? "Alterações não salvas" : "Tudo salvo"}
            </span>
          </p>

          <div className="flex items-center gap-3">
            <button
              onClick={descartar}
              disabled={!temAlteracao || salvando}
              className="text-sm text-slate-500 transition-colors hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Descartar
            </button>
            <button
              onClick={salvar}
              disabled={!temAlteracao || salvando}
              className="h-10 rounded-lg bg-brand-sidebar px-4 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
            >
              {salvando ? "Salvando…" : "Salvar alterações"}
            </button>
          </div>
        </div>
      }
    >
      <div className="grid h-[460px] grid-cols-[232px_1fr]">
        {/* NAVEGAÇÃO */}
        <nav className="flex flex-col justify-between border-r border-slate-100 bg-slate-50/60 px-3 py-4">
          <ul className="space-y-1">
            {MENU.map((item) => {
              const ativa = secao === item.chave;
              return (
                <li key={item.chave}>
                  <button
                    onClick={() => setSecao(item.chave)}
                    className={`flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left transition-colors ${
                      ativa
                        ? "bg-white shadow-sm ring-1 ring-slate-200"
                        : "hover:bg-white/70"
                    }`}
                  >
                    <span className="min-w-0">
                      <span
                        className={`block truncate text-sm ${
                          ativa ? "font-semibold text-slate-900" : "text-slate-600"
                        }`}
                      >
                        {item.label}
                      </span>
                      <span className="block truncate text-[11px] text-slate-400">
                        {item.apoio}
                      </span>
                    </span>
                    {item.selo && <Selo {...item.selo} />}
                  </button>
                </li>
              );
            })}
          </ul>

          {conta && (
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
              <p className="truncate text-xs font-semibold text-slate-800">{conta.nome}</p>
              <p className="truncate text-[11px] text-slate-400">{conta.email}</p>
            </div>
          )}
        </nav>

        {/* CONTEÚDO */}
        <div className="overflow-y-auto px-6 py-5">
          <h3 className="text-base font-semibold text-slate-900">
            {cabecalho[secao].titulo}
          </h3>
          <p className="mt-0.5 text-xs text-slate-400">{cabecalho[secao].apoio}</p>

          <div className="mt-5">
            {secao === "geral" && (
              <div className="max-w-xs">
                <InputSelect
                  label={t("modal_settings.field.language")}
                  placeholder={t("modal_settings.field.language")}
                  options={LANGUAGES_LIST}
                  value={idioma}
                  onValueChange={trocarIdioma}
                />
                <p className="mt-2 text-[11px] text-slate-400">
                  A troca vale na hora, não precisa salvar.
                </p>
              </div>
            )}

            {secao === "integracoes" && (
              <div className="space-y-3">
                <CartaoIntegracao
                  icone={<Store className="h-4 w-4" />}
                  cor="#008060"
                  nome="Shopify"
                  conectada={shopify?.conectada ?? false}
                  carregando={shopify === null}
                  detalhe={
                    shopify?.loja ??
                    "Receba os pedidos da sua loja prontos para cotar."
                  }
                  rotuloAcao={shopify?.conectada ? "Gerenciar" : "Conectar loja"}
                  onAcao={() =>
                    navigate(
                      shopify?.conectada ? paths.shopifyProdutos : paths.formShopify,
                    )
                  }
                />
                <CartaoIntegracao
                  icone={<Plug className="h-4 w-4" />}
                  cor="#1F2B45"
                  nome="Nuvemshop"
                  conectada={nuvemshop?.conectada ?? false}
                  carregando={nuvemshop === null}
                  detalhe="Calcule o frete internacional automaticamente no checkout da sua loja."
                  rotuloAcao={nuvemshop?.conectada ? "Gerenciar" : "Conectar loja"}
                  onAcao={() => navigate(paths.nuvemshopConectar)}
                />
                <p className="text-[11px] leading-relaxed text-slate-400">
                  Pedidos das lojas conectadas aparecem em “Realizar cotação” com itens,
                  valores e endereço já preenchidos.
                </p>
              </div>
            )}

            {secao === "empresa" && (
              <>
                <div className="grid grid-cols-3 gap-4">
                  <Campo
                    label="Telefone"
                    valor={empresa.telefoneCelular}
                    aoMudar={(v) => setEmpresa((p) => ({ ...p, telefoneCelular: v }))}
                    placeholder="(00) 00000-0000"
                  />
                  <Campo
                    label="Rua"
                    valor={empresa.enderecoRua}
                    aoMudar={(v) => setEmpresa((p) => ({ ...p, enderecoRua: v }))}
                    placeholder="Rua, avenida..."
                  />
                  <Campo
                    label="Número"
                    valor={empresa.enderecoNumero}
                    aoMudar={(v) => setEmpresa((p) => ({ ...p, enderecoNumero: v }))}
                    placeholder="123"
                  />
                  <Campo
                    label="CEP"
                    valor={empresa.enderecoCEP}
                    aoMudar={(v) => setEmpresa((p) => ({ ...p, enderecoCEP: v }))}
                    placeholder="00000-000"
                  />
                  <Campo
                    label="Complemento"
                    valor={empresa.enderecoComplemento || ""}
                    aoMudar={(v) => setEmpresa((p) => ({ ...p, enderecoComplemento: v }))}
                    placeholder="Opcional"
                  />
                  <Campo
                    label="Cidade"
                    valor={empresa.enderecoCidade}
                    aoMudar={(v) => setEmpresa((p) => ({ ...p, enderecoCidade: v }))}
                    placeholder="Cidade"
                  />
                  <Campo
                    label="Estado"
                    valor={empresa.enderecoEstado}
                    aoMudar={(v) =>
                      setEmpresa((p) => ({ ...p, enderecoEstado: v.toUpperCase() }))
                    }
                    placeholder="UF"
                  />
                  <Campo
                    label="País"
                    valor={empresa.enderecoPais}
                    aoMudar={(v) => setEmpresa((p) => ({ ...p, enderecoPais: v }))}
                    placeholder="BR"
                  />
                </div>

                <Aviso>
                  Este endereço aparece como <strong>remetente</strong> nas cotações e como{" "}
                  <strong>local de coleta padrão</strong>.
                </Aviso>
              </>
            )}

            {secao === "remetente" && (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800">
                      Usar o endereço da empresa como remetente
                    </p>
                    <p className="mt-0.5 text-[11px] leading-snug text-slate-400">
                      Desative se você despacha de endereços diferentes. Nome e telefone
                      continuam preenchidos.
                    </p>
                  </div>
                  <Interruptor ativo={remetenteFixoAtivo} onMudar={alternarRemetente} />
                </div>

                {remetenteFixoAtivo && (
                  <div className="flex items-start gap-3 rounded-xl bg-slate-50 px-4 py-3">
                    <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-white text-brand-primary ring-1 ring-slate-200">
                      <MapPin className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                        Remetente nas cotações
                      </p>
                      <p className="truncate text-sm font-semibold text-slate-800">
                        {conta?.nome ?? "—"}
                        {empresaSalva.telefoneCelular
                          ? ` · ${empresaSalva.telefoneCelular}`
                          : ""}
                      </p>
                      <p className="truncate text-[11px] text-slate-500">
                        {[
                          [empresaSalva.enderecoRua, empresaSalva.enderecoNumero]
                            .filter(Boolean)
                            .join(", "),
                          empresaSalva.enderecoCidade,
                          empresaSalva.enderecoEstado,
                          empresaSalva.enderecoCEP,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    </div>
                    <button
                      onClick={() => setSecao("empresa")}
                      className="shrink-0 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
                    >
                      Editar
                    </button>
                  </div>
                )}
              </div>
            )}

            {secao === "destinatario" && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <Campo
                    label="Nome do destinatário"
                    valor={destino.destinoFixoNome}
                    aoMudar={(v) => setDestino((p) => ({ ...p, destinoFixoNome: v }))}
                    placeholder="Quem recebe"
                  />
                  <Campo
                    label="Telefone"
                    valor={destino.destinoFixoTelefone}
                    aoMudar={(v) => setDestino((p) => ({ ...p, destinoFixoTelefone: v }))}
                    placeholder="Telefone do destinatário"
                  />
                  <Campo
                    label="Rua"
                    valor={destino.destinoFixoRua}
                    aoMudar={(v) => setDestino((p) => ({ ...p, destinoFixoRua: v }))}
                    placeholder="Rua, número"
                  />
                  <Campo
                    label="CEP / código postal"
                    valor={destino.destinoFixoCEP}
                    aoMudar={(v) => setDestino((p) => ({ ...p, destinoFixoCEP: v }))}
                    placeholder="00000"
                  />
                  <Campo
                    label="Cidade"
                    valor={destino.destinoFixoCidade}
                    aoMudar={(v) => setDestino((p) => ({ ...p, destinoFixoCidade: v }))}
                    placeholder="Cidade"
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <Campo
                      label="Estado"
                      valor={destino.destinoFixoEstado}
                      aoMudar={(v) => setDestino((p) => ({ ...p, destinoFixoEstado: v }))}
                      placeholder="Estado"
                    />
                    <Campo
                      label="País"
                      valor={destino.destinoFixoPais}
                      aoMudar={(v) => setDestino((p) => ({ ...p, destinoFixoPais: v }))}
                      placeholder="US"
                    />
                  </div>
                </div>

                <Aviso>
                  Preenchido, entra automaticamente como destinatário em cada nova cotação
                  — e continua editável lá. Deixe em branco para desligar.
                </Aviso>
              </>
            )}
          </div>
        </div>
      </div>
    </ModalBase>
  );
};

export default ModalSettings;

/* ------------------------------------------------------------------ */

type ReactNodeSelo = { texto: string; tom: "verde" | "cinza" };

const Selo = ({ texto, tom }: ReactNodeSelo) => (
  <span
    className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold ${
      tom === "verde" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"
    }`}
  >
    {texto}
  </span>
);

const Campo = ({
  label,
  valor,
  aoMudar,
  placeholder,
}: {
  label: string;
  valor: string;
  aoMudar: (v: string) => void;
  placeholder?: string;
}) => (
  <label className="block">
    <span className="mb-1.5 block text-[11px] font-medium text-slate-500">{label}</span>
    <input
      value={valor}
      onChange={(e) => aoMudar(e.target.value)}
      placeholder={placeholder}
      className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition-colors placeholder:text-slate-300 focus:border-brand-primary focus:ring-1 focus:ring-brand-primary"
    />
  </label>
);

const Aviso = ({ children }: { children: React.ReactNode }) => (
  <div className="mt-5 flex items-start gap-2.5 rounded-lg bg-blue-50/70 px-3 py-2.5">
    <Building2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-primary" />
    <p className="text-[11px] leading-relaxed text-slate-600">{children}</p>
  </div>
);

const Interruptor = ({
  ativo,
  onMudar,
}: {
  ativo: boolean;
  onMudar: (v: boolean) => void;
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={ativo}
    onClick={() => onMudar(!ativo)}
    className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
      ativo ? "bg-emerald-500" : "bg-slate-300"
    }`}
  >
    <span
      className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
        ativo ? "translate-x-5" : "translate-x-0.5"
      }`}
    />
  </button>
);

const CartaoIntegracao = ({
  icone,
  cor,
  nome,
  conectada,
  carregando,
  detalhe,
  rotuloAcao,
  onAcao,
}: {
  icone: React.ReactNode;
  cor: string;
  nome: string;
  conectada: boolean;
  carregando: boolean;
  detalhe: string;
  rotuloAcao: string;
  onAcao: () => void;
}) => (
  <div className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3">
    <div
      className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-white"
      style={{ backgroundColor: cor }}
    >
      {icone}
    </div>

    <div className="min-w-0 flex-1">
      <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
        {nome}
        {carregando ? (
          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-400">
            verificando…
          </span>
        ) : (
          <span
            className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${
              conectada ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"
            }`}
          >
            {conectada && <Check className="h-2.5 w-2.5" />}
            {conectada ? "Conectada" : "Não conectada"}
          </span>
        )}
      </p>
      <p className="truncate text-[11px] text-slate-400">{detalhe}</p>
    </div>

    <button
      onClick={onAcao}
      className={`shrink-0 rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${
        conectada
          ? "border border-slate-200 text-slate-600 hover:bg-slate-50"
          : "bg-brand-sidebar text-white hover:opacity-90"
      }`}
    >
      {rotuloAcao}
    </button>
  </div>
);
